/** The LTP column: what a leg is marked at, and the one cell you can type over.
 *
 * Operator, 2026-11-04: *"LTP also I want an option to edit the way we do on
 * entry"* and *"whatever edit, based on that PNL will update"*. What an edit
 * means depends on the leg:
 *
 *   open    a price typed for THIS MINUTE replaces the archive's. The server
 *           prices the whole basket from it, so the row, the total and the
 *           curve all follow. Step the clock and the archive's price returns.
 *   closed  the shown price is the one it was BOOKED at, so editing it edits
 *           the exit price; the realised P&L moves with it.
 *   dormant not taken yet — nothing to edit.
 */

import type { ReactElement } from "react";
import { cn } from "@/lib/cn";
import type { SimPayoff } from "@/lib/simulatorApi";
import { CommitNumberInput } from "./CommitNumberInput";
import { hhmm } from "./positionsFormat";

type PricedLeg = SimPayoff["legs"][number];

const INPUT = "input h-6 w-20 px-1 py-0 text-right font-mono text-xs tabular-nums";

/** ⚠️ A leg outside the archived slice is priced by Black-Scholes at the
 * chain's own vol (operator, 2026-09-23). That number is a MODEL, so it is
 * marked with a ~ and says so on hover: a calculated premium that reads like
 * a quoted one is how a backtest ends up trusting prices nobody could get. */
function ModelledTilde(): ReactElement {
  return (
    <span
      className="text-warning"
      title="Calculated, not recorded: this strike is outside the archived chain, so it is priced by Black-Scholes at the chain's own volatility."
    >
      ~
    </span>
  );
}

/** A typed price is neither a traded one nor a modelled one, so it says so and
 * offers the way back to the archive's. */
function EditedTag({
  name,
  onReset,
}: Readonly<{ name: string; onReset: () => void }>): ReactElement {
  return (
    <span className="inline-flex items-center gap-0.5 text-[10px] text-accent">
      edited
      <button
        type="button"
        className="btn btn-ghost px-1 py-0 text-[10px] leading-none"
        aria-label={`Reset price, ${name}`}
        title="Back to the archive's price for this minute"
        onClick={onReset}
      >
        ↺
      </button>
    </span>
  );
}

function ClosedMark({
  leg,
  name,
  onExit,
}: Readonly<{ leg: PricedLeg; name: string; onExit: (n: number) => void }>): ReactElement {
  return (
    <span
      className="inline-flex items-center justify-end gap-1"
      title={`Closed at ${hhmm(leg.exit_at ?? "")}`}
    >
      <CommitNumberInput
        label={`Exit price, ${name}`}
        className={INPUT}
        min={0}
        step={1}
        value={leg.exit ?? null}
        onCommit={onExit}
      />
      <span className="text-[10px] text-ink-muted">closed</span>
    </span>
  );
}

function OpenMark({
  leg,
  name,
  onMark,
  onResetMark,
}: Readonly<{
  leg: PricedLeg;
  name: string;
  onMark: (n: number) => void;
  onResetMark: () => void;
}>): ReactElement {
  return (
    <span className="inline-flex items-center justify-end gap-1">
      {leg.modelled === true && <ModelledTilde />}
      <CommitNumberInput
        label={`LTP, ${name}`}
        className={cn(INPUT, leg.modelled === true && "text-warning")}
        min={0}
        step={1}
        value={leg.ltp}
        onCommit={onMark}
      />
      {leg.edited === true && <EditedTag name={name} onReset={onResetMark} />}
    </span>
  );
}

export function MarkCell({
  leg,
  closed,
  dormant,
  onMark,
  onExit,
  onResetMark,
}: Readonly<{
  leg: PricedLeg;
  closed: boolean;
  dormant: boolean;
  onMark: (n: number) => void;
  onExit: (n: number) => void;
  onResetMark: () => void;
}>): ReactElement {
  if (dormant) {
    return (
      <td className="px-2 py-1.5 text-right">
        <span
          className="text-[10px] text-ink-muted"
          title={`Not taken until ${hhmm(leg.entry_at ?? "")}`}
        >
          from {hhmm(leg.entry_at ?? "")}
        </span>
      </td>
    );
  }
  const name = `${String(leg.strike)} ${leg.side}`;
  return (
    <td className="px-2 py-1.5 text-right">
      {closed ? (
        <ClosedMark leg={leg} name={name} onExit={onExit} />
      ) : (
        <OpenMark leg={leg} name={name} onMark={onMark} onResetMark={onResetMark} />
      )}
    </td>
  );
}
